import { useMemo, useState, useContext } from 'react';
import useDialogFocus from '@discover-ui/dialog/useDialogFocus';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '@discover-data/stores/restaurant/model/times/default';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { useStoreActions, useStoreState } from '@discover-data/stores/restaurant/hooks';
import DEFAULT_HAS_LAST_TABLE from '@discover-data/stores/restaurant/model/hasLastTable/default';
import DEFAULT_SESSION from '@discover-data/stores/restaurant/model/session/default';
import { areRestaurantStatusesDefault } from '@discover-data/stores/restaurant/model/restaurantStatuses/default';
import DEFAULT_TAG_IDS from '@discover-data/stores/restaurant/model/tagIds/default';
import getCitySlug from '@discover-lib/getCitySlug';
import getTagOptions from '@discover-data/stores/hooks/tagIds/getOptions';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import useHasLastTable from '@discover-data/stores/hooks/hasLastTable/useHasLastTable';
import useResetFilters from '@discover-data/stores/hooks/useResetFilters';
import useSession from '@discover-data/stores/hooks/session/useSession';
import useShouldShowFavouriteFilter from '@discover-data/stores/hooks/favourite/useShouldShow';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import useTagIds from '@discover-data/stores/hooks/tagIds/useTagIds';
import { cn } from '@discover-lib/utils';
import Button from '@discover-ui/button';
import {
  DialogRoot,
  type DialogRootProps,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from '@discover-ui/dialog';
import FilterIcon from '@discover-ui/icons/filter.svg';
import buttonVariants, { type ButtonVariantProps } from '@discover-ui/button/variants';
import Separator from '@discover-ui/separator';
import Label from '@discover-ui/label';
import DiscountOnlySwitch from '@discover-components/restaurant/filters/discountOnly/switch';
import ExitRegularOnlyButton from '@discover-components/restaurant/list/exitRegularTabeOnlyButton';
import FavouriteOnlySwitch from '@discover-components/restaurant/filters/favouriteOnly/switch';
import FiltersAccordion from '@discover-components/restaurant/filters/accordion';
import SortSelect from '@discover-components/restaurant/filters/sortSelect';
import SuburbSelect from '@discover-components/restaurant/filters/suburb/select';
import RestaurantStatusesFilter, {
  useCanManageRestaurantStatusFilter,
} from '@discover-components/restaurant/filters/restaurantStatuses/filter';
import TimeRangeFilter from '@discover-components/restaurant/filters/time/range';
import SilverstripeContext from '@/components/context/Silverstripe';

export type RestaurantFiltersDrawerProps = { tags: RestaurantTagType[] } & Omit<
  DialogRootProps,
  'open' | 'onOpenChange'
> & {
    triggerClassName?: string;
    triggerProps?: ButtonVariantProps;
    suburbs: SuburbType[];
  };

export default function RestaurantFiltersDrawer({
  tags,
  triggerClassName,
  triggerProps = {},
  suburbs,
  ...props
}: RestaurantFiltersDrawerProps) {
  const shouldShowFavourite = useShouldShowFavouriteFilter();
  const discoverEnabled = useStoreState((state) => state.discoverEnabled);
  const regularOnly = useStoreState((state) => state.regularOnly.value);
  const discountOnly = useStoreState((state) => state.discountOnly.value);
  const restaurantStatusesValue = useStoreState((state) => state.restaurantStatuses.value);

  const dialogFocus = useDialogFocus();
  const [open, setOpen] = useState(false);

  const [tagIds] = useTagIds(tags);
  const selectedTagIds = tagIds.filter((tagId) => !DEFAULT_TAG_IDS.includes(tagId));

  const isSuburbDefault = useStoreState((state) => state.suburb.isDefault);

  const [hasLastTable] = useHasLastTable();
  const hasLastTableSelected = hasLastTable !== DEFAULT_HAS_LAST_TABLE;

  const [startTime] = useStartTime();
  const hasStartTime = startTime !== DEFAULT_START_TIME;

  const [endTime] = useEndTime();
  const hasEndTime = endTime !== DEFAULT_END_TIME;

  const showVenueStatusFilter = useCanManageRestaurantStatusFilter();
  const hasNonDefaultVenueStatuses =
    showVenueStatusFilter && !areRestaurantStatusesDefault(restaurantStatusesValue);

  const appliedFilters = [
    ...selectedTagIds,
    !isSuburbDefault,
    hasLastTableSelected,
    hasStartTime,
    hasEndTime,
    regularOnly,
    discountOnly,
    hasNonDefaultVenueStatuses,
  ].filter((filterValue) => !!filterValue);

  const filterCount = appliedFilters.length;
  const hasFilters = filterCount > 0;

  // only resetting the router state and not the store since the sync hooks will update the store and we need to set all the router values in one go (a limitation of the useRouterState hook)
  const { optionKeys } = getTagOptions(tags);
  const routerKeys = useMemo(
    () => [
      'slug',
      'has-last-table',
      'suburb',
      ...optionKeys, // tags
      'start-time',
      'end-time',
    ],
    [optionKeys]
  );

  const [session] = useSession();
  const { page } = useContext(SilverstripeContext);

  const resetFilters = useResetFilters(routerKeys);

  // can't use the useSuburb hook to update the store value since it will cause an additional router state change
  const resetStoreSuburb = useStoreActions((state) => state.suburb.reset);
  const resetRestaurantStatuses = useStoreActions((state) => state.restaurantStatuses.reset);

  return (
    <DialogRoot {...props} open={open} onOpenChange={setOpen}>
      <DialogTrigger
        role="combobox"
        aria-label="Open filter menu"
        aria-expanded={open}
        className={cn(
          buttonVariants({ ...triggerProps, kind: hasFilters ? 'normal' : 'outline' }),
          'gap-1',
          triggerClassName
        )}>
        <FilterIcon className="size-4" />
        {filterCount > 0 ? (
          <>
            <strong>·</strong>
            <span>{filterCount}</span>
          </>
        ) : null}
      </DialogTrigger>
      <DialogContent
        {...dialogFocus}
        className="max-h-[85vh] supports-[height:100dvh]:max-h-[85dvh]">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
          <VisuallyHidden.Root>
            <DialogDescription>A list of search filters</DialogDescription>
          </VisuallyHidden.Root>
        </DialogHeader>
        <div className="-mr-1 flex flex-1 flex-col overflow-y-auto px-1 pr-2 pt-1">
          {shouldShowFavourite && (
            <>
              <FavouriteOnlySwitch />
              <Separator className="my-3" />
            </>
          )}
          {discoverEnabled && !regularOnly && (
            <>
              <DiscountOnlySwitch />
              <Separator className="my-3" />
            </>
          )}
          {regularOnly && (
            <>
              <ExitRegularOnlyButton />
              <Separator className="my-3" />
            </>
          )}
          <div className="flex flex-row items-center justify-between">
            <Label className="w-1/2 text-base font-medium">Sort by</Label>
            <SortSelect
              triggerProps={{
                kind: 'outline',
                scale: 'sm',
                round: true,
              }}
              className="w-1/2 justify-center font-normal"
            />
          </div>
          <Separator className="my-3" />
          <div className="flex flex-row items-center justify-between">
            <Label className="w-1/2 text-base font-medium">Suburb</Label>
            <SuburbSelect
              suburbs={suburbs}
              triggerProps={{
                kind: isSuburbDefault ? 'outline' : 'normal',
                scale: 'sm',
                round: true,
              }}
              className="w-1/2 justify-center font-normal"
            />
          </div>
          <Separator className="my-3" />
          <TimeRangeFilter />
          <Separator className="my-3" />
          <FiltersAccordion className="mb-3" tags={tags} renderCollapsedOptions={false} />
          <RestaurantStatusesFilter />
        </div>
        <DialogFooter className="flex flex-row gap-1">
          <Button
            width="wide"
            kind={hasFilters ? 'outline' : 'gray'}
            disabled={!hasFilters}
            onClick={() => {
              // unset the suburb slug
              const defaultSuburbSlug = page?.breadcrumbs?.edges?.[0]?.node?.slug;
              const slug = getCitySlug(null, defaultSuburbSlug, session, DEFAULT_SESSION);

              // reset store suburb
              resetStoreSuburb();

              if (showVenueStatusFilter) {
                resetRestaurantStatuses();
              }

              resetFilters({
                pathname: slug,
              });
            }}>
            Reset all
            {hasFilters ? (
              <span>
                <strong>&nbsp;·&nbsp;</strong>
                {filterCount}
              </span>
            ) : null}
          </Button>
          <DialogClose asChild>
            <Button width="wide" kind="normal">
              Done
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
